import { getStemLabel } from 'player/src/utils/utils'
import { ChannelPlayer } from 'player'
import { TaskOperations } from '../../../types'

const getBestOperation = (
  operations: TaskOperations[],
  opName: string
): TaskOperations | undefined => {
  return (
    operations.find(
      (op: TaskOperations) =>
        op.name.startsWith(opName) && op.status === 'COMPLETED'
    ) || operations.find((op: TaskOperations) => op.name.startsWith(opName))
  )
}

const getBestOperationPreferred = (
  operations: TaskOperations[],
  opName: string,
  preferredSeparationType: string
): TaskOperations | undefined => {
  return (
    operations.find(
      (op: TaskOperations) =>
        op.name.startsWith(opName) &&
        ((preferredSeparationType === 'custom' && op.name === opName) ||
          op.params.type === preferredSeparationType) &&
        op.status === 'COMPLETED'
    ) ||
    operations.find(
      (op: TaskOperations) =>
        op.name.startsWith(opName) &&
        ((preferredSeparationType === 'custom' && op.name === opName) ||
          op.params.type === preferredSeparationType)
    )
  )
}

export const getTrackPreference = (trackId?: string): string | null => {
  return trackId
    ? global.window.sessionStorage.getItem(`moises:track:${trackId}:preference`)
    : null
}

export const getTrackSeparate = (
  operations: TaskOperations[],
  trackId?: string
): TaskOperations | undefined => {
  if (!trackId) return undefined

  const preferredSeparationType = getTrackPreference(trackId)
  const operationName =
    preferredSeparationType === 'custom' ? 'SEPARATE_CUSTOM' : 'SEPARATE_'

  if (preferredSeparationType) {
    return getBestOperationPreferred(
      operations,
      operationName,
      preferredSeparationType
    )
  }

  return getBestOperation(operations, operationName)
}

export const getTrackOperations = ({
  allOperations,
  trackId
}: {
  trackId: string
  allOperations: TaskOperations[]
}): TaskOperations[] => {
  const operations = [...allOperations]
    .sort(
      (opA, opB) =>
        new Date(opA.completedAt || opA.startedAt || opA.createdAt).getTime() -
        new Date(opB.completedAt || opB.startedAt || opB.createdAt).getTime()
    )
    .reverse()

  return [
    getTrackSeparate(operations, trackId),
    getBestOperation(operations, 'BEATSCHORDS_') as TaskOperations,
    getBestOperation(operations, 'FINGERPRINT_'),
    getBestOperation(operations, 'SEGMENTATION_'),
    getBestOperation(operations, 'LYRICS_')
  ].filter((op) => op) as TaskOperations[]
}

export const getInstrumentsDescription = (
  stems: ChannelPlayer[],
  i18n: any
): string => {
  return stems
    .map((stem) => {
      return getStemLabel(stem.id, i18n)
    })
    .join(', ')
}

export const getLoadingState = (
  operationType: string,
  allOperations: TaskOperations[]
): boolean => {
  if (operationType === 'custom') {
    return !!allOperations.find(
      (op) =>
        op.name === 'SEPARATE_CUSTOM' &&
        ['QUEUED', 'STARTED'].includes(op.status)
    )
  }

  return !!allOperations.find(
    (op) =>
      op.params.type === operationType &&
      ['QUEUED', 'STARTED'].includes(op.status)
  )
}

export const getCompletedState = (
  operationType: string,
  allOperations: TaskOperations[]
): boolean => {
  if (operationType === 'custom') {
    return !!allOperations.find(
      (op) => op.name === 'SEPARATE_CUSTOM' && op.status === 'COMPLETED'
    )
  }

  return !!allOperations.find(
    (op) => op.params.type === operationType && op.status === 'COMPLETED'
  )
}
